import type { KeyboardEventHandler, RefObject } from 'react';
import { useEffect, useState } from 'react';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import type { DropdownOption } from '../text-dropdown';

interface UseDropdownNavigationOptions {
  open: boolean;
  options: DropdownOption[];
  localValue: string;
  nearestOption?: DropdownOption | null;
  parseInputValue?: (inputValue: string) => string;
  onNavigate?: (option: DropdownOption) => void;
  onSelect?: (value: string) => void;
  inputControlRef?: RefObject<TextControlBaseInstance | null>;
}

interface UseDropdownNavigationType {
  highlightedIndex: number;
  navigate: (direction: 'next' | 'prev') => void;
  resetHighlight: () => void;
  handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export function useDropdownNavigation({
  open,
  options,
  localValue,
  nearestOption,
  parseInputValue,
  onNavigate,
  onSelect,
  inputControlRef
}: UseDropdownNavigationOptions): UseDropdownNavigationType {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const getNextIndex = (currentIndex: number, direction: 'next' | 'prev') => {
    if (direction === 'next') {
      return currentIndex < options.length - 1 ? currentIndex + 1 : 0;
    }

    return currentIndex > 0 ? currentIndex - 1 : options.length - 1;
  };

  const findOptionIndex = (searchValue: string) => {
    const indexByValue = options.findIndex(
      (option) => option.value === searchValue
    );
    if (indexByValue >= 0) {
      return indexByValue;
    }

    return options.findIndex((option) => option.label === searchValue);
  };

  const calculateNextIndex = (
    currentIndex: number,
    direction: 'next' | 'prev'
  ) => {
    const isUnselected = currentIndex === -1;
    const nearestIndex =
      isUnselected && nearestOption ? findOptionIndex(nearestOption.value) : -1;

    if (nearestIndex >= 0 && nearestOption) {
      onNavigate?.(nearestOption);

      return nearestIndex;
    }

    const nextIndex = getNextIndex(currentIndex, direction);
    const option = options[nextIndex];

    if (option) {
      onNavigate?.(option);
    }

    return nextIndex;
  };

  const navigate = (direction: 'next' | 'prev') => {
    setHighlightedIndex((currentIndex) =>
      calculateNextIndex(currentIndex, direction)
    );
  };

  const resetHighlight = () => {
    setHighlightedIndex(-1);
  };

  const handleKeyDown: KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    if (!open) return;

    if (e.key === 'Tab') {
      e.preventDefault();
      navigate(e.shiftKey ? 'prev' : 'next');
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      inputControlRef?.current?.inputRef.current?.blur();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      navigate('next');
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      navigate('prev');
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();

      if (highlightedIndex >= 0 && highlightedIndex < options.length) {
        onSelect?.(options[highlightedIndex].value);
      }

      inputControlRef?.current?.inputRef.current?.blur();
    }
  };

  useEffect(() => {
    if (!open) {
      setHighlightedIndex(-1);
      return;
    }

    const currentOptionIndex = findOptionIndex(localValue);
    if (currentOptionIndex >= 0) {
      setHighlightedIndex(currentOptionIndex);
      return;
    }

    const canUseFormatted = parseInputValue && nearestOption;
    const formattedValue = canUseFormatted ? parseInputValue(localValue) : '';
    const isFormattedMatch =
      canUseFormatted &&
      (formattedValue === nearestOption.value ||
        formattedValue === nearestOption.label);
    const nearestOptionIndex = isFormattedMatch
      ? findOptionIndex(nearestOption.value)
      : -1;
    const hasValidNearestIndex = nearestOptionIndex >= 0;

    if (canUseFormatted && isFormattedMatch && hasValidNearestIndex) {
      setHighlightedIndex(nearestOptionIndex);
      return;
    }

    setHighlightedIndex(-1);
  }, [open, options, localValue, nearestOption, parseInputValue]);

  return {
    highlightedIndex,
    navigate,
    resetHighlight,
    handleKeyDown
  };
}
